import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { IdUtils } from '@ecm/consumer';
import { EcmDispatcherModule } from '@ecm/dispatcher';
import { EcmMaterialModule, PhoneNumberPipe } from '@ecm/material';
import { PortfolioService } from '@ecm/portfolio';
import { EcmRouterModule, Link, LinkService, TitleService, TrackingData } from '@ecm/router';
import { KenobiParams, Status } from '@ecm/tracker';

import { ContractExclusion } from '../../../../../business/api/contract/contractExclusion';
import { TelcoStateEnum } from '../../../../../business/api/contract/telcoState.enum';
import { Dashboard } from '../../../../../business/api/dashboard/dashboard';
import { CrossroadsViewModelMapper } from '../../../../../business/mapper/crossroads/crossroads.view-model-mapper';
import { ActiveContractViewModel } from '../../../../../business/model/crossroads/contracts-options/active-contract.view-model';
import { SuggestedContractViewModel } from '../../../../../business/model/crossroads/contracts-options/suggested-contract.view-model';
import { CrossroadsViewModel } from '../../../../../business/model/crossroads/crossroads.view-model';
import { KenobiCodes } from '../../../../../business/model/tracking/kenobiCodes';
import { TrackedPage } from '../../../../../business/model/tracking/trackedPage.enum';
import { ClearCacheService } from '../../../../../services/clearCache.service';
import { RemoveContractService } from '../../../../../services/removeContract.service';
import { RhocService } from '../../../../../services/rhoc.service';
import { TrackingService } from '../../../../../services/tracking.service';
import { UrlService } from '../../../../../services/url.service';
import { EnvEnum } from '../../../../../shared/enums/env.enum';
import { AbstractParentComponent } from '../../../../shared/abstract-parent.component';
import { RemoveContractUtils } from '../removeContract.utils';
import { FeedbackViewModel } from './feedback.view-model';

@Component({
    selector: 'remove-contract',
    templateUrl: 'remove-contract.html',
    standalone: true,
    imports: [EcmMaterialModule, AsyncPipe, EcmRouterModule, TranslateModule, PhoneNumberPipe, NgTemplateOutlet],
})
export class RemoveContractComponent extends AbstractParentComponent implements OnInit {
    private readonly trackingService = inject(TrackingService);
    private readonly rhocService = inject(RhocService);
    private readonly contractService = inject(RemoveContractService);
    private readonly portfolioService = inject(PortfolioService);
    private readonly titleService = inject(TitleService);
    private readonly translate = inject(TranslateService);
    private readonly router = inject(Router);
    private readonly linkService = inject(LinkService);
    private readonly clearCacheService = inject(ClearCacheService);
    protected urlService = inject(UrlService);

    /**
     * Transaction properties for BCO monitoring
     */
    private readonly pageId: string;

    public suggestedContractViewModel: SuggestedContractViewModel | null = null;
    public info: string | undefined;
    public detail: string | undefined;
    public asAnExclusionAlert: boolean | undefined;
    public canDisplayAlert = false;
    public feedback: FeedbackViewModel | null = null;
    public removeContractTranslations: any;

    public currentUrl: string | undefined;

    public contractSelectorLink: Link | undefined;

    public removeContractText = 'retirer-contrat';

    public constructor() {
        super();
        this.translate.use('fr');
        this.pageId = IdUtils.generateRandomId();
    }

    public ngOnInit(): void {
        super.ngOnInit();
        this.currentUrl = this.urlService.getCurrentUrl();
        const cid: string = this.route.snapshot.params['cid'];
        this.translate.get('dashboard').subscribe((dashboardTranslations: any) => {
            this.removeContractTranslations = dashboardTranslations['removeContract'];
            this.titleService.setTitle(dashboardTranslations['removeContract']['title']);
            EcmDispatcherModule.sendBackRoute(this.removeContractText);

            this.portfolioService.getRawContracts().subscribe((contracts) => {
                const crossroadsViewModel: CrossroadsViewModel = CrossroadsViewModelMapper.getCrossroadsViewModel(
                    new Dashboard(),
                    contracts,
                    dashboardTranslations['contractsAndOptions'],
                );
                const removeContracts: SuggestedContractViewModel[] = this.builtSuggestedInfo(
                    RemoveContractUtils.extractActiveContract(crossroadsViewModel.contracts),
                );
                const removeSuggestedContracts: SuggestedContractViewModel[] = crossroadsViewModel.suggestedContracts;

                const contractWithCid = removeSuggestedContracts
                    .concat(removeContracts)
                    .filter((contract: SuggestedContractViewModel) => contract.cid === cid);
                if (contractWithCid.length === 0) {
                    this.trackView(KenobiCodes.MSUPCTR, Status.KORG, cid);
                    this.router.navigate([this.removeContractText]);
                } else {
                    this.suggestedContractViewModel = contractWithCid[0];
                    this.manageContract(this.suggestedContractViewModel, this.removeContractTranslations);
                }
                this.contractSelectorLink = { inApp: this.removeContractText };
            });
        });
    }

    /**
     * sent trackView
     * @param state
     * @param cid
     */
    public manageTracking(state: string, cid: string): void {
        switch (state) {
            case TelcoStateEnum.SUGGESTED: {
                this.trackView(KenobiCodes.CNAFFRAP, Status.OK);
                break;
            }
            case TelcoStateEnum.LOGGED: {
                this.trackView(KenobiCodes.SUPCTRBL, Status.OK, cid);
                break;
            }
            default: {
                this.trackView(KenobiCodes.MSUPCTR, Status.OK, cid);
                break;
            }
        }
    }

    /**
     * built a SuggestedContract type array from activeContractviewModel array
     * @param {ActiveContractViewModel[]} contracts
     * @return {SuggestedContractViewModel[]}
     */
    public builtSuggestedInfo(contracts: ActiveContractViewModel[]): SuggestedContractViewModel[] {
        const lightActiveContract: SuggestedContractViewModel[] = [];
        for (const contract of contracts) {
            lightActiveContract.push(
                Object.assign(new SuggestedContractViewModel(), {
                    offerName: contract.offerName,
                    detail: contract.detail,
                    cid: contract.cid,
                    state: contract.state,
                    role: contract.role,
                    type: contract.type,
                    brand: contract.brand,
                    member: contract.member,
                }),
            );
        }
        return lightActiveContract;
    }

    /**
     * Submit contract removal
     */
    public validateSuppression(): void {
        this.contractService.removeContract(this.suggestedContractViewModel.cid).subscribe({
            next: (response: any) => {
                this.trackLink(this.removeContractTranslations['submit'], '/retirer-contrat-OK');
                this.trackView(KenobiCodes.PSUPCTR, Status.OK, this.suggestedContractViewModel.cid);
                this.trackingService.trackRemoveContractView(
                    'confirmation',
                    'confirmation_retirer_un_contrat',
                    'Z48Dlfiz',
                );
                // clear cash to reaload dashboard and contract
                // this.clearCacheService.clearCache('DASHBOARD.DASHBOARD_URL').subscribe();
                // this.clearCacheService.clearCache('CONTRACTS_URL').subscribe();
                this.clearCacheService.clear([{ apiKey: EnvEnum.DASHBOARD_URL }]);
                this.clearCacheService.clear([{ apiKey: EnvEnum.CONTRACTS_URL }]);
                this.getFeedbackData(true, response['message'], response['subMessage']);
            },
            error: (error: Error) => {
                this.trackLink(this.removeContractTranslations['submit'], '/retirer-contrat-KO');
                this.trackView(KenobiCodes.PSUPCTR, Status.KOSI, this.suggestedContractViewModel.cid);
                this.trackingService.trackRemoveContractView(
                    'erreur',
                    'rc_erreur_votre_choix_na_pu_etre_memorise',
                    'UxVMbzN2',
                );
                this.getFeedbackData(
                    false,
                    error['customerMessage']['message'],
                    error['customerMessage']['subMessage'],
                );
            },
        });
    }

    /**
     * Acces home page
     */
    public backToHome(): void {
        this.linkService.openLink(
            { inApp: this.feedback.link },
            Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.removeContract,
                    trackingArea: 'care_retirer_ce_contrat',
                    trackingName: this.feedback.confirmationLabel,
                },
            }),
        );
    }

    /**
     * Acces contract selector
     */
    public backToContractSelector(title: string): void {
        this.linkService.openLink(
            { inApp: this.removeContractText },
            Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.removeContract,
                    trackingArea: 'care_retirer_ce_contrat',
                    trackingName: title,
                },
            }),
        );
    }

    /**
     * Check contract to compute display labels
     * @param {ActiveContractViewModel} contract
     * @param {any} removeContractTranslations
     */
    private manageContract(contract: SuggestedContractViewModel, removeContractTranslations: any): void {
        if (contract.state === TelcoStateEnum.LOGGED) {
            this.info = removeContractTranslations['info']['logged'];
            this.canDisplayAlert = true;
            this.trackView(KenobiCodes.SUPCTRBL, Status.OK, contract.cid);
        } else {
            this.rhocService
                .getRhoc(contract.cid, KenobiCodes.MSUPCTR, this.pageId)
                .subscribe((contractExclusions: ContractExclusion[]) => {
                    if (contractExclusions && contractExclusions.length > 0) {
                        this.asAnExclusionAlert = true;
                        this.info = contractExclusions[0].label;
                    } else {
                        this.info = removeContractTranslations['info']['default'];
                    }
                    if (contract.state === TelcoStateEnum.DELEGATED) {
                        this.detail = removeContractTranslations['detail']['delegated'];
                    } else if (
                        contract.state === TelcoStateEnum.HELD &&
                        contract.type === 'open' &&
                        contract.brand === 'Orange'
                    ) {
                        this.detail = removeContractTranslations['detail']['held'];
                    } else {
                        this.detail = removeContractTranslations['detail']['default'];
                    }
                    this.canDisplayAlert = true;
                    this.manageTracking(contract.state, contract.cid);
                });
        }
        this.trackingService.trackRemoveContractView('information', 'validation_retirer_un_contrat', 'r5MXCxDA');
    }

    /**
     * Generate feedback for success or error
     * @param {boolean} success
     * @param {string} message
     * @param {string} subMessage
     */
    private getFeedbackData(success: boolean, message: string, subMessage: string): void {
        this.feedback = Object.assign(new FeedbackViewModel(), {
            style: success ? 'success' : 'alert',
            title: message,
            detail: subMessage,
            confirmationStyle: 'button-secondary',
            confirmationLabel: "Revenir à l'accueil",
            link: '/accueil',
        });
    }

    private trackView(kenobiCode: any, status: Status, cid?: string): void {
        this.trackingService.trackKenobi(
            new KenobiParams(kenobiCode['functionality'], kenobiCode['rubric'], status),
            this.pageId,
            false,
            cid ? cid : 'undefined',
        );
    }

    /**
     * Track click for TMS
     * @param {string} label
     * @param {string} targetUrl
     */
    private trackLink(label: string, targetUrl: string) {
        this.trackingService.trackRemoveContractLink('care_retirer_ce_contrat', label, targetUrl);
    }
}
